<template>
    <div>
        <loading v-if="!isMeetingInitialized" />

        <div id="zoom-client-element" />

        <!-- <heat-map
            v-if="cIsUserAdmin"
            class="heat-map"
        /> -->
    </div>
</template>

<script>
// https://socket.io/docs/v4/client-api/
import { io } from 'socket.io-client'

import { ZoomSignatureController } from '../controllers/ZoomSignatureController'
import { SocketTicketController } from '../controllers/SocketTicketController'
import { ZoomMtgController } from '../controllers/ZoomMtgController'
import { WatermarkController } from '../controllers/WatermarkController'
import { IpAddressController } from '../controllers/IpAddressController'
import { UserDataController } from '../controllers/UserDataController'

import { JoinedMeetingEvent } from '../events/JoinedMeetingEvent'
import { MeetingEndedEvent } from '../events/MeetingEndedEvent'

import http from '../http'

import Loading from './Loading.vue'
// import HeatMap from './HeatMap.vue'

export default {
    components: {
        Loading
        // HeatMap
    },
    name: 'LaiZoomClient',

    props: {
        userId: {
            type: Number,
            default: 0
        },

        courseId: {
            type: Number,
            default: 0
        },

        liveClassId: {
            type: Number,
            default: 0
        },

        leaveUrl: {
            type: String,
            default: ''
        },

        fingerprint: {
            type: String,
            default: ''
        }
    },

    data () {
        return {
            appApiUrl: process.env.VUE_APP_API_URL,
            socketUrl: process.env.VUE_APP_SOCKET_URL,
            zoomVersion: process.env.VUE_APP_ZOOM_VERSION,

            socket: {},

            userRoles: ['STUDENT'],
            tc: '',
            ipAddress: '',

            isDependeciesLoaded: false,
            isMeetingInitialized: false,

            // Guards endLiveClass so ending the live class runs exactly once.
            lessonEnded: false
        }
    },

    computed: {
        cIsUserHost () {
            return this.cIsUserAdmin || this.cIsUserTeacher
        },

        cIsUserAdmin () {
            return this.userRoles.includes('ADMIN')
        },

        cIsUserTeacher () {
            return this.userRoles.includes('TEACHER')
        },

        cIsAllowedToBoot () {
            // No token here anymore (ADR 0007): the BFF authenticates via the shared
            // session cookie, so booting only needs the class/user context.
            return this.courseId > 0 && this.liveClassId > 0 && this.userId && this.leaveUrl
        },

        cSignatureApi () {
            return `${this.appApiUrl}/v2/courses/${this.courseId}/live-classes/${this.liveClassId}/${this.cIsUserHost ? 'teacher-signature' : 'signature'}/`
        },

        // True once the realtime socket is connected (io() replaces the {} default with an
        // instance that has .emit). Gates both "don't reconnect" and "safe to emit".
        cIsSocketAvailable () {
            return this.socket && typeof this.socket.emit === 'function'
        }
    },

    watch: {
        cIsAllowedToBoot (newValue) {
            if (newValue) {
                this.bootApp()
            }
        }
    },

    methods: {
        initSocketServer () {
            // Idempotent: startApp may run more than once before the meeting boots. The
            // guard holds because io() is assigned synchronously below — no await between
            // the check and the assignment where a second call could slip a stale socket in.
            if (this.cIsSocketAvailable) {
                return
            }

            // Ownership-gated ticket so socket-server accepts our initiate-course (ADR 0007).
            // Authenticated by the BFF via the shared session cookie (no token). `auth` is a
            // provider socket.io calls on every (re)connect, so each attempt — including
            // reconnects after the 60s ticket expired — carries a fresh ticket; a null ticket
            // still connects (allowed during rollout, can't emit once enforced).
            const ticketController = new SocketTicketController(
                `${this.appApiUrl}/v2/courses/${this.courseId}/live-classes/socket-ticket/`,
                this.fingerprint
            )

            this.socket = io(this.socketUrl, { auth: ticketController.authProvider() })
        },

        async bootApp () {
            if (!this.isDependeciesLoaded) {
                await this.loadDependencies()
            }

            if (!this.isMeetingInitialized) {
                this.startApp()
            }
        },

        async loadDependencies () {
            window.ZoomMtg.setZoomJSLib(`https://source.zoom.us/${this.zoomVersion}/lib`, '/av')
            await window.ZoomMtg.preLoadWasm()
            await window.ZoomMtg.prepareWebSDK()

            // kendi türkçe dil objemiz "/lang/tr-TR.json" bu dosya üzerinde düzenleme yapılabilir.
            // Must call ZoomMtg.i18n.load before ZoomMtg.init to make internationalization and localization (i18n) work.
            await window.ZoomMtg.i18n.load('/lang/tr-TR.json', 'tr-TR')

            this.isDependeciesLoaded = true
        },

        async startApp () {
            if (this.cIsAllowedToBoot) {
                await this.fetchUserData()

                // Connect the socket here (not in mounted): the ticket needs the
                // courseId, only guaranteed once cIsAllowedToBoot is true. Synchronous —
                // the auth provider fetches each ticket lazily on (re)connect.
                this.initSocketServer()

                this.bootZoomClient()

                document.addEventListener(JoinedMeetingEvent.name, () => {
                    this.isMeetingInitialized = true

                    // Any host (teacher OR admin) starting the class notifies students so
                    // their "Derse Katıl" appears and the site-wide live banner fires — not
                    // teachers only, or an admin-run class would signal no one.
                    if (this.cIsUserHost) {
                        this.informStudents()
                    }
                    if (!this.cIsUserHost) {
                        this.setWatermarks()
                    }
                })

                // The LAST host left the meeting (ZoomMtgController only fires this once
                // the live host count hits 0, so a co-host leaving while others remain
                // does not) → end the live class automatically.
                document.addEventListener(MeetingEndedEvent.name, () => {
                    if (this.cIsUserHost) {
                        this.endLiveClass()
                    }
                })
            }
        },

        // Mark the live class not-live and broadcast so students and the teacher's panel
        // drop out — the same effect as tip-app's "Dersi Bitir", triggered when the last
        // host leaves the Zoom meeting. Authenticated by the BFF via the session cookie
        // (no token); the socket relays end-course → course-finished. Runs once.
        async endLiveClass () {
            if (this.lessonEnded) {
                return
            }
            this.lessonEnded = true

            try {
                const headers = this.fingerprint ? { 'X-Device-Fingerprint': this.fingerprint } : {}
                await http.patch(
                    `${this.appApiUrl}/v2/courses/${this.courseId}/live-classes/${this.liveClassId}/`,
                    { is_live: false },
                    { headers }
                )

                if (this.cIsSocketAvailable) {
                    this.socket.emit('end-course', { courseId: this.courseId, liveClassId: this.liveClassId })
                }
            } catch (error) {
                console.log(error)
            }
        },

        async bootZoomClient () {
            const signatureController = new ZoomSignatureController(this.cSignatureApi, this.fingerprint)
            await signatureController.getSignature()

            const mtgController = new ZoomMtgController(this.leaveUrl)
            if (this.cIsUserHost) {
                mtgController.authorizeForHost(this.cIsUserTeacher)
            } else {
                mtgController.authorizeForAttendee()
            }
            mtgController.initMeeting(signatureController.getMeetingConfigs())
        },

        informStudents () {
            // ögrencileri de egitime katabilmek icin app icerisinde buton gösterelim.
            // liveClassId'yi de yolla ki panel tam bu oturumu canlıya çeksin (aynı anda
            // birden fazla oturum açıksa yanlış olanı işaretlemesin).
            this.socket.emit('initiate-course', { courseId: this.courseId, liveClassId: this.liveClassId })
        },

        async fetchUserData () {
            const ipAddressController = new IpAddressController(this.appApiUrl)
            await ipAddressController.fetchFromAPi()
            this.ipAddress = ipAddressController.getIpAddress()

            const userDataController = new UserDataController(this.appApiUrl, this.userId)
            await userDataController.fetchFromAPi()
            this.tc = userDataController.getTc()
            this.userRoles = userDataController.getUserRoles()
        },

        setWatermarks () {
            const watermarkController = new WatermarkController()
            watermarkController.setWatermarks([
                {
                    text: this.tc
                },
                {
                    text: this.ipAddress,
                    style: {
                        top: '50px'
                    }
                }
            ])
        }
    },

    async mounted () {
        // initSocketServer moved into startApp — the socket ticket needs courseId,
        // which arrives via props and is only ready once cIsAllowedToBoot flips true.
        this.bootApp()
    },

    beforeDestroy () {
        clearInterval(this.joinAudioInterval)
        this.joinAudioInterval = null
    }
}
</script>

<style>
#zmmtg-root {
    display: none;
}

#zoom-client-element {
    pointer-events: auto;
    z-index: 999999999999;
    background-color: transparent;
}

/* footer butonların soldaki ilk bölümü. kamera ve mikrofon aç-kapat */
html:not(.meeting-host) footer#wc-footer > .footer__inner > div:first-child > * {
    display: none !important;
}

/* ekranın sol üst köşesindeki görüşme hakkında bilgilendirme ve görüşmenin kaydı ile ilgili pause stop vb. butonlar */
.meeting-info-container.meeting-info-container--left-side {
    display: none !important;
}

/* Ekran genişliği daraldığında alttaki butonlar Daha Fazla isimli bir buton altına giriyor. */
html:not(.meeting-host) .footer__inner .more-button {
    display: none !important;
}

/* Ekranın alt kısmındaki "Güvenlik" etiketli buton */
html:not(.meeting-host) .security-option-menu {
    display: none !important;
}

/* Ekranın alt kısmındaki "Katılımcılar" etiketli buton */
html.meeting-teacher [feature-type*="participants"],
html:not(.meeting-host) [feature-type*="participants"] {
    display: none !important;
}

/* Ekranın alt kısmındaki "Anketler" etiketli buton */
html:not(.meeting-host) [feature-type*="Poll"] {
    display: none !important;
}

/* Ekranın alt kısmındaki "Ekran Paylaş" etiketli buton */
html:not(.meeting-host) [feature-type*="sharing"] {
    display: none !important;
}

/* Ekranın alt kısmındaki "Pause/Stop Recording" etiketli butonlar */
html:not(.meeting-host) [feature-type*="record"] {
    display: none !important;
}

/* Ekranın alt kısmındaki "AI Companion" etiketli buton */
html:not(.meeting-host) [feature-type*="AICompanion"] {
    display: none !important;
}

/* Ekranın alt kısmındaki "Reactions" etiketli buton */
html:not(.meeting-host) [feature-type*="reaction"] {
    display: none !important;
}

/* Ekranın alt kısmındaki "Diger" etiketli buton */
html:not(.meeting-host) [feature-type*="more"] {
    display: none !important;
}

/* Ekranın alt kısmındaki "Altyazilar" etiketli buton */
html:not(.meeting-host) [feature-type*="newLTT"] {
    display: none !important;
}

/* notification manager */
html:not(.meeting-host) #notificationManager {
    display: none !important;
}

/* kullanici chat kisminda diger kisilerin isimlerini görmesin */
html:not(.meeting-host) section.chat-receiver-list__scrollbar {
    display: none;
}

.heat-map {
    position: absolute;
    top: 64px;
    left: 16px;
    width: 100%;
    height: 100%;
    z-index: 9999999999999;
    pointer-events: none;
}
</style>
